/**
 * Chart configuration and data transformation helpers for the Dashboard.
 */

/**
 * Build Chart.js options with auto-scaled Y axis.
 * @param {object} chartData - Chart.js data object with datasets
 * @returns {object} Chart.js options
 */
export const getChartOptions = (chartData) => {
  let maxValue = 0;
  if (chartData?.datasets) {
    chartData.datasets.forEach((dataset) => {
      const datasetMax = Math.max(...dataset.data);
      if (datasetMax > maxValue) {
        maxValue = datasetMax;
      }
    });
  }

  const suggestedMax = Math.ceil(maxValue * 1.1);

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: suggestedMax > 5 ? suggestedMax : undefined,
        ticks: {
          precision: 0,
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          }
        }
      },
      x: {
        ticks: { maxRotation: 45, minRotation: 0 }
      }
    },
    interaction: { intersect: false, mode: 'index' }
  };
};

const DATASET_COLORS = {
  packageDetails: {
    borderColor: 'rgb(0, 180, 216)',
    backgroundColor: 'rgba(0, 180, 216, 0.2)'
  },
  shipmentTracking: {
    borderColor: 'rgb(99, 179, 237)',
    backgroundColor: 'rgba(99, 179, 237, 0.2)'
  }
};

/**
 * Transform monthly API data into Chart.js data format.
 */
export const buildMonthlyChartData = (monthlyData) => {
  if (!monthlyData) return null;

  const packageMonths = monthlyData.packageDetails?.months || [];
  const shipmentMonths = monthlyData.zoeShipmentTracking?.months || [];
  const labels = packageMonths.map((item) => item.monthName || item.month);

  return {
    labels,
    datasets: [
      {
        label: 'Package Details',
        data: packageMonths.map((item) => item.total || 0),
        ...DATASET_COLORS.packageDetails,
        tension: 0.1
      },
      {
        label: 'Zoe Shipment Tracking',
        data: shipmentMonths.map((item) => item.total || 0),
        ...DATASET_COLORS.shipmentTracking,
        tension: 0.1
      }
    ]
  };
};

/**
 * Transform daily API data into Chart.js data format.
 */
export const buildDailyChartData = (dailyData) => {
  if (!dailyData?.packageDetails || !dailyData?.zoeShipmentTracking) {
    return null;
  }

  const packageDays = dailyData.packageDetails.days || [];
  const shipmentDays = dailyData.zoeShipmentTracking.days || [];
  const labels = packageDays.map(
    (day) => `${day.day} (${day.dayName.slice(0, 3)})`
  );

  return {
    labels,
    datasets: [
      {
        label: 'Package Details',
        data: packageDays.map((day) => day.total || 0),
        ...DATASET_COLORS.packageDetails,
        tension: 0.1
      },
      {
        label: 'Zoe Shipment Tracking',
        data: shipmentDays.map((day) => day.total || 0),
        ...DATASET_COLORS.shipmentTracking,
        tension: 0.1
      }
    ]
  };
};
